import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheckIcon, ZapIcon, DollarSignIcon, ActivityIcon, CheckIcon, NetworkIcon, GlobeIcon, WrenchIcon, ArrowRightIcon, MapIcon, HardHatIcon, CpuIcon, Building2Icon, LandmarkIcon, ServerIcon, Share2Icon, GitMergeIcon, ClockIcon, HelpCircleIcon, ChevronDownIcon } from 'lucide-react';
const stats = [{
  label: 'RELIABILITY',
  value: '99.999%',
  desc: 'Ethernet networks offer an impressive uptime of up to 99.999%, ensuring that your business remains connected with minimal downtime.',
  icon: ActivityIcon,
  color: 'text-green-500',
  bg: 'bg-green-50'
}, {
  label: 'COST EFFECTIVE',
  value: 'Up to 50%',
  desc: 'Switching to Ethernet can reduce networking costs by up to 50% compared to older technologies like T1 lines, especially as bandwidth needs grow.',
  icon: DollarSignIcon,
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  label: 'SECURITY',
  value: '70%',
  desc: '70% of businesses report improved data security with Ethernet services, thanks to private, dedicated connections that minimize the risk of cyber threats.',
  icon: ShieldCheckIcon,
  color: 'text-purple-500',
  bg: 'bg-purple-50'
}, {
  label: 'SPEED',
  value: '100 Gbps',
  desc: 'Ethernet services can support data transfer speeds ranging from 1 Mbps to 100 Gbps, making them ideal for businesses of all sizes and needs.',
  icon: ZapIcon,
  color: 'text-amber-500',
  bg: 'bg-amber-50'
}];
const keyFeatures = ['Single Ethernet interface', 'VLAN Transparency', 'Scalable capacity', 'Secure, private connection', '24/7 priority support', 'Performance reporting', 'Symmetrical connectivity'];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4
    }
  }
};
export function EthernetFiberPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  return <div data-id="element-1434">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-1435">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-1436"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-1437"></div>
        <div className="absolute top-0 left-1/3 -translate-y-1/4 w-[700px] h-[700px] bg-purple-500/15 rounded-full blur-[120px] pointer-events-none" data-id="element-1438"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1439">
          <div className="max-w-3xl" data-id="element-1440">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1441">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1442">
                <span className="flex h-2 w-2 rounded-full bg-purple-400 mr-2" data-id="element-1443"></span>
                Dedicated High-Performance Networking
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-1444">
                Dedicated
                <br className="hidden md:block" data-id="element-1445" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-1446">
                  Ethernet & Fiber
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8" data-id="element-1447">
                Enterprise-grade Ethernet solutions with dedicated bandwidth,
                symmetrical speeds, and 99.999% uptime — built for businesses
                that can't afford to slow down.
              </p>
              <button onClick={() => onNavigate('home')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-1448">
                Get a Custom Quote
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1449" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-white" data-id="element-1450">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1451">
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} data-id="element-1452">
            {stats.map((stat, i) => {
            const Icon = stat.icon;
            return <motion.div key={i} variants={itemVariants} className="text-center" data-id="element-1453">
                  <div className={`w-14 h-14 ${stat.bg} rounded-2xl flex items-center justify-center mx-auto mb-4`} data-id="element-1454">
                    <Icon className={`w-7 h-7 ${stat.color}`} data-id="element-1455" />
                  </div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2" data-id="element-1456">
                    {stat.label}
                  </div>
                  <div className="text-3xl font-extrabold text-slate-900 mb-3" data-id="element-1457">
                    {stat.value}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-1458">
                    {stat.desc}
                  </p>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* Network Topology Options */}
      <section className="py-24 bg-slate-50" data-id="element-1459">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1460">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1461">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1462">
              Flexible Network Topologies
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1463">
              We design and provision Ethernet networks that match your exact
              organizational structure and data flow requirements.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1464">
            {[{
            icon: GitMergeIcon,
            title: 'Point-to-Point (E-Line)',
            desc: 'A secure, dedicated connection between two locations. Perfect for connecting a branch office directly to a headquarters or data center.'
          }, {
            icon: Share2Icon,
            title: 'Point-to-Multipoint (E-Tree)',
            desc: 'Connects a central hub to multiple branch locations. Ideal for hub-and-spoke organizational models.'
          }, {
            icon: NetworkIcon,
            title: 'Multipoint-to-Multipoint (E-LAN)',
            desc: 'Creates a seamless wide area network where all locations can communicate directly with each other as if on the same local network.'
          }].map((topology, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1465">
                <div className="w-14 h-14 bg-purple-50 rounded-xl flex items-center justify-center mb-6" data-id="element-1466">
                  <topology.icon className="w-7 h-7 text-purple-600" data-id="element-1467" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1468">
                  {topology.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-1469">
                  {topology.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-slate-900 relative overflow-hidden" data-id="element-1470">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-1471"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1472">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1473">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1474">
              The Path to Dedicated Fiber
            </h2>
            <p className="text-lg text-slate-400" data-id="element-1475">
              Building a dedicated fiber network requires precision. We manage
              the entire lifecycle of your connection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-1476">
            {[{
            title: 'Site Survey',
            desc: 'We perform a physical and digital audit of your location to determine fiber availability and pathing.',
            icon: MapIcon
          }, {
            title: 'Engineering',
            desc: 'Our engineers design the network architecture to ensure maximum redundancy and performance.',
            icon: CpuIcon
          }, {
            title: 'Construction',
            desc: 'We coordinate the physical build-out, including permitting and fiber pulling to your suite.',
            icon: HardHatIcon
          }, {
            title: 'Activation',
            desc: 'Final testing and hand-off of your high-speed, dedicated circuit with performance certification.',
            icon: ZapIcon
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="text-center" data-id="element-1477">
                <div className="w-16 h-16 bg-white/10 border border-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm" data-id="element-1478">
                  <step.icon className="w-8 h-8 text-primary" data-id="element-1479" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-1480">
                  {step.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-1481">
                  {step.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section className="py-24 bg-white" data-id="element-1482">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1483">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1484">
            <div className="lg:col-span-1" data-id="element-1485">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-1486">
                Built for Scale
              </h2>
              <p className="text-lg text-slate-600 mb-8" data-id="element-1487">
                Dedicated fiber is the gold standard for organizations with
                massive data requirements.
              </p>
              <button onClick={() => onNavigate('contact')} className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-xl font-bold transition-colors" data-id="element-1488">
                Discuss Your Needs
              </button>
            </div>

            {[{
            title: 'Data Centers',
            desc: 'Ultra-low latency backbones and massive throughput for mission-critical hosting.',
            icon: ServerIcon
          }, {
            title: 'Finance',
            desc: 'Secure, private circuits for high-frequency trading and sensitive financial data transfer.',
            icon: LandmarkIcon
          }, {
            title: 'Large Campus',
            desc: 'Connecting multiple buildings with high-capacity fiber for unified campus networking.',
            icon: Building2Icon
          }].map((industry, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-slate-50 p-8 rounded-3xl border border-slate-100" data-id="element-1489">
                <industry.icon className="w-12 h-12 text-primary mb-6" data-id="element-1490" />
                <h3 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1491">
                  {industry.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-1492">
                  {industry.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Key Features + Description */}
      <section className="py-20 bg-slate-50" data-id="element-1493">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1494">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-1495">
            {/* Key Features */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1496">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8" data-id="element-1497">
                Key Features
              </h2>
              <div className="space-y-4" data-id="element-1498">
                {keyFeatures.map((feature, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.3,
                delay: i * 0.05
              }} className="flex items-center bg-white rounded-xl px-5 py-4 border border-slate-100 shadow-sm" data-id="element-1499">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mr-4" data-id="element-1500">
                      <CheckIcon className="w-4 h-4 text-primary" data-id="element-1501" />
                    </div>
                    <span className="text-slate-800 font-medium" data-id="element-1502">
                      {feature}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Description */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="lg:pt-4" data-id="element-1503">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-1504">
                Enterprise-Grade Networking
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-1505">
                Ethernet Network Services provide businesses with a robust,
                high-performance networking solution tailored to the needs of
                modern enterprises. Whether your goal is to link multiple office
                locations, support critical cloud applications, or ensure smooth
                communication across your organization, our Ethernet services
                deliver the reliability and scalability required to keep your
                business running efficiently.
              </p>
              <div className="relative rounded-2xl overflow-hidden shadow-xl" data-id="element-1506">
                <img src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&h=500&fit=crop" alt="Fiber optic cables" className="w-full h-auto object-cover" data-id="element-1507" />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/30 to-transparent" data-id="element-1508"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Ethernet */}
      <section className="py-20 bg-white" data-id="element-1509">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1510">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-1511">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-1512">
              Why Ethernet Network Services?
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed" data-id="element-1513">
              Ethernet Network Services offer your business unmatched speed,
              reliability, and security. With up to 10x faster data transfer
              rates, an impressive 99.999% uptime, and robust, private
              connections, Ethernet solutions ensure seamless operations and
              top-notch data protection. Scalable and cost-effective, they also
              enhance global connectivity by up to 40%, supporting your business
              growth and collaboration.
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-1514">
            {[{
            label: '10x faster',
            desc: 'Data transfer rates compared to legacy solutions'
          }, {
            label: '99.999%',
            desc: 'Network uptime guarantee'
          }, {
            label: '40% boost',
            desc: 'In global connectivity and collaboration'
          }, {
            label: 'Private',
            desc: 'Dedicated connections for maximum security'
          }].map((item, i) => <motion.div key={i} variants={itemVariants} className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-1515">
                <div className="text-2xl font-extrabold text-primary mb-2" data-id="element-1516">
                  {item.label}
                </div>
                <p className="text-slate-600" data-id="element-1517">{item.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* SLA & Performance Guarantees */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-1518">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-[100px]" data-id="element-1519"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1520">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1521">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1522">
              Uncompromising Performance Guarantees
            </h2>
            <p className="text-lg text-slate-400" data-id="element-1523">
              Our dedicated fiber circuits come with strict Service Level
              Agreements (SLAs) ensuring your data arrives fast and intact.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1524">
            {[{
            icon: ActivityIcon,
            value: '< 5ms',
            label: 'Latency',
            desc: 'Ultra-low delay for real-time applications.'
          }, {
            icon: ZapIcon,
            value: '< 1ms',
            label: 'Jitter',
            desc: 'Consistent packet delivery for flawless voice/video.'
          }, {
            icon: ShieldCheckIcon,
            value: '< 0.1%',
            label: 'Packet Loss',
            desc: 'Virtually zero data loss across the network.'
          }, {
            icon: ClockIcon,
            value: '4 hrs',
            label: 'MTTR',
            desc: 'Mean Time To Repair guarantee for hardware issues.'
          }].map((stat, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center" data-id="element-1525">
                <stat.icon className="w-8 h-8 text-purple-400 mx-auto mb-4" data-id="element-1526" />
                <div className="text-3xl font-extrabold text-white mb-1" data-id="element-1527">
                  {stat.value}
                </div>
                <div className="text-sm font-bold text-purple-400 uppercase tracking-wider mb-3" data-id="element-1528">
                  {stat.label}
                </div>
                <p className="text-slate-400 text-sm" data-id="element-1529">{stat.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Choose Ambient Telecom */}
      <section className="py-20 bg-slate-50" data-id="element-1530">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1531">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-1532">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1533">
              Why Choose Ambient Telecom?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-1534">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm" data-id="element-1535">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5" data-id="element-1536">
                <GlobeIcon className="w-6 h-6 text-primary" data-id="element-1537" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1538">
                Global Reach
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1539">
                Providing a comprehensive range of connectivity solutions with a
                global reach, ensuring your business stays connected no matter
                where you operate.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm" data-id="element-1540">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5" data-id="element-1541">
                <WrenchIcon className="w-6 h-6 text-primary" data-id="element-1542" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1543">
                Custom Solutions
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1544">
                Developing custom solutions tailored specifically to meet your
                unique business requirements, ensuring you get exactly the
                connectivity you need.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ethernet FAQ */}
      <section className="py-24 bg-white" data-id="element-1545">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1546">
          <div className="text-center mb-16" data-id="element-1547">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-1548">
              <HelpCircleIcon className="w-8 h-8 text-primary" data-id="element-1549" />
              Ethernet & Fiber FAQ
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1550">
              Common questions about dedicated enterprise connectivity.
            </p>
          </div>
          <div className="space-y-4" data-id="element-1551">
            {[{
            q: "What's the difference between Dedicated Ethernet and Broadband?",
            a: 'Broadband is a shared connection where speeds fluctuate based on neighborhood traffic. Dedicated Ethernet is a private, uncontended connection providing guaranteed symmetrical speeds 24/7.'
          }, {
            q: 'How long does installation take?',
            a: 'If fiber is already lit in your building, installation can take 30-45 days. If new construction is required to bring fiber to your suite, it typically takes 60-90 days.'
          }, {
            q: 'Can I upgrade my bandwidth later?',
            a: 'Yes. Most Ethernet circuits are delivered on a larger physical port (e.g., a 100Mbps circuit on a 1Gbps port). Upgrading bandwidth usually just requires a quick logical change by the carrier without new hardware.'
          }, {
            q: 'Is dedicated internet worth the extra cost?',
            a: 'For businesses that rely heavily on cloud applications, VoIP, video conferencing, or large file transfers, the productivity gained from guaranteed uptime and speed far outweighs the cost difference.'
          }, {
            q: 'What about redundancy?',
            a: 'We highly recommend a redundant setup. We can provision a primary dedicated fiber circuit and a secondary broadband or 4G/5G LTE backup from a different carrier to ensure 100% uptime.'
          }].map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-slate-50" data-id="element-1552">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-white transition-colors" data-id="element-1553">
                  <span className="font-bold text-slate-900" data-id="element-1554">{faq.q}</span>
                  <motion.div animate={{
                rotate: activeFaq === i ? 180 : 0
              }} data-id="element-1555">
                    <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-1556" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-1557">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden bg-white" data-id="element-1558">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100" data-id="element-1559">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary relative overflow-hidden py-20" data-id="element-1560">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-1561">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl" data-id="element-1562"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl" data-id="element-1563"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" data-id="element-1564">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1565">
            Ready to upgrade to Ethernet-Fiber?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-1566">
            Let our experts analyze your current setup and show you how
            dedicated Ethernet can reduce costs while boosting performance.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1567">
            <button onClick={() => onNavigate('home')} className="w-full sm:w-auto bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-1568">
              Get Your Free Analysis
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1569" />
            </button>
            <a href="tel:+4435451747" className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg transition-colors" data-id="element-1570">
              Call Us: (443) 545-1747
            </a>
          </div>
        </div>
      </section>
    </div>;
}